import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Calendar, Download, Filter } from 'lucide-react';
import { DataStore } from '@/lib/data-store';
import { exportToExcel } from '@/lib/export-utils';

const Analytics = () => {
  const data = DataStore.getData();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedMetric, setSelectedMetric] = useState('revenue');

  // Calculate comprehensive statistics
  const stats = useMemo(() => {
    const invoices = data.invoices || [];
    const projects = data.projects || [];
    const customers = data.customers || [];
    const costs = data.costs || [];
    const reports = data.reports || [];

    const totalRevenue = invoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
    const totalCosts = costs.reduce((sum, c) => sum + (c.jumlah || 0), 0);
    const totalProfit = totalRevenue - totalCosts;
    const profitMargin = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100) : 0;

    // Revenue by project
    const revenueByProject = projects.map(p => ({
      name: p.namaProjek.substring(0, 20),
      revenue: invoices
        .filter(inv => inv.projek === p.namaProjek)
        .reduce((sum, inv) => sum + (inv.total || 0), 0),
      cost: p.kosProjek,
      profit: (invoices
        .filter(inv => inv.projek === p.namaProjek)
        .reduce((sum, inv) => sum + (inv.total || 0), 0)) - p.kosProjek
    })).filter(item => item.revenue > 0 || item.cost > 0);

    // Revenue trend (monthly)
    const monthlyRevenue: Record<string, number> = {};
    invoices.forEach(inv => {
      if (inv.tarikhInvoice) {
        const month = inv.tarikhInvoice.substring(0, 7);
        monthlyRevenue[month] = (monthlyRevenue[month] || 0) + (inv.total || 0);
      }
    });

    const revenueTrend = Object.entries(monthlyRevenue)
      .sort()
      .map(([month, revenue]) => ({
        month,
        revenue,
        target: 50000
      }));

    // Customer analysis
    const customerMetrics = customers.map(c => ({
      name: c.nama,
      invoices: invoices.filter(inv => inv.pelanggan === c.nama).length,
      totalSpent: invoices
        .filter(inv => inv.pelanggan === c.nama)
        .reduce((sum, inv) => sum + (inv.total || 0), 0)
    })).filter(c => c.totalSpent > 0).sort((a, b) => b.totalSpent - a.totalSpent).slice(0, 10);

    // Project status breakdown
    const projectStatus = {
      completed: projects.filter(p => p.status === 'Selesai').length,
      inProgress: projects.filter(p => p.status === 'Dalam Proses').length,
      tender: projects.filter(p => p.status === 'Tender').length,
      cancelled: projects.filter(p => p.status === 'Dibatalkan').length,
    };

    return {
      totalRevenue,
      totalCosts,
      totalProfit,
      profitMargin,
      invoiceCount: invoices.length,
      projectCount: projects.length,
      customerCount: customers.length,
      revenueByProject,
      revenueTrend,
      customerMetrics,
      projectStatus,
      avgInvoiceValue: invoices.length > 0 ? totalRevenue / invoices.length : 0,
      avgProjectValue: projects.length > 0 ? totalRevenue / projects.length : 0
    };
  }, [data]);

  const projectStatusData = [
    { name: 'Selesai', value: stats.projectStatus.completed, color: '#10b981' },
    { name: 'Dalam Proses', value: stats.projectStatus.inProgress, color: '#3b82f6' },
    { name: 'Tender', value: stats.projectStatus.tender, color: '#f59e0b' },
    { name: 'Dibatalkan', value: stats.projectStatus.cancelled, color: '#ef4444' },
  ];

  const handleExportAnalytics = () => {
    const analyticsData = [
      {
        'Metric': 'Total Revenue',
        'Value': `RM ${stats.totalRevenue.toFixed(2)}`
      },
      {
        'Metric': 'Total Costs',
        'Value': `RM ${stats.totalCosts.toFixed(2)}`
      },
      {
        'Metric': 'Total Profit',
        'Value': `RM ${stats.totalProfit.toFixed(2)}`
      },
      {
        'Metric': 'Profit Margin',
        'Value': `${stats.profitMargin.toFixed(2)}%`
      },
      {
        'Metric': 'Number of Invoices',
        'Value': stats.invoiceCount
      },
      {
        'Metric': 'Number of Projects',
        'Value': stats.projectCount
      },
      {
        'Metric': 'Number of Customers',
        'Value': stats.customerCount
      },
      {
        'Metric': 'Average Invoice Value',
        'Value': `RM ${stats.avgInvoiceValue.toFixed(2)}`
      },
      {
        'Metric': 'Average Project Value',
        'Value': `RM ${stats.avgProjectValue.toFixed(2)}`
      }
    ];

    exportToExcel(analyticsData, 'Analytics Report');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Analytics & Insights</h1>
          <p className="text-slate-600 mt-1">Analisis mendalam performa bisnis SAP Ngecat</p>
        </div>
        <Button 
          onClick={handleExportAnalytics}
          className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-4 py-2 h-auto"
        >
          <Download size={18} />
          <span className="font-semibold">Export Report</span>
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Total Revenue</p>
              <p className="text-2xl font-bold text-blue-600">RM {stats.totalRevenue.toFixed(0)}</p>
              <p className="text-xs text-slate-500">{stats.invoiceCount} invoices</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Total Costs</p>
              <p className="text-2xl font-bold text-orange-600">RM {stats.totalCosts.toFixed(0)}</p>
              <p className="text-xs text-slate-500">Operational</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Net Profit</p>
              <p className={`text-2xl font-bold ${stats.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                RM {stats.totalProfit.toFixed(0)}
              </p>
              <p className="text-xs text-slate-500">{stats.profitMargin.toFixed(1)}% margin</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Active Projects</p>
              <p className="text-2xl font-bold text-purple-600">{stats.projectStatus.inProgress}</p>
              <p className="text-xs text-slate-500">of {stats.projectCount} total</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Active Customers</p>
              <p className="text-2xl font-bold text-indigo-600">{stats.customerCount}</p>
              <p className="text-xs text-slate-500">Client base</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Revenue Trend (Monthly)</CardTitle>
            <CardDescription>Income progression over time</CardDescription>
          </CardHeader>
          <CardContent>
            {stats.revenueTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={stats.revenueTrend}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => `RM ${value.toFixed(0)}`} />
                  <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-80 flex items-center justify-center text-slate-500">No data available</div>
            )}
          </CardContent>
        </Card>

        {/* Project Status */}
        <Card>
          <CardHeader>
            <CardTitle>Project Status Distribution</CardTitle>
            <CardDescription>Breakdown by completion status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              <ResponsiveContainer width={200} height={200}>
                <PieChart>
                  <Pie
                    data={projectStatusData}
                    cx={100}
                    cy={100}
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {projectStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3">
                {projectStatusData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                    <span className="text-sm text-slate-600">{item.name}</span>
                    <span className="text-sm font-bold text-slate-900 ml-auto">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue by Project */}
      {stats.revenueByProject.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Revenue by Project</CardTitle>
            <CardDescription>Top performing projects</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.revenueByProject.sort((a, b) => b.profit - a.profit).slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip formatter={(value) => `RM ${value.toFixed(0)}`} />
                <Legend />
                <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                <Bar dataKey="cost" fill="#f97316" name="Cost" />
                <Bar dataKey="profit" fill="#10b981" name="Profit" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Top Customers */}
      {stats.customerMetrics.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top Customers by Revenue</CardTitle>
            <CardDescription>Customer spending analysis</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.customerMetrics.map((customer, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                  <div>
                    <p className="font-medium text-slate-900">{customer.name}</p>
                    <p className="text-sm text-slate-600">{customer.invoices} invoices</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-slate-900">RM {customer.totalSpent.toFixed(2)}</p>
                    <div className="w-32 h-2 bg-slate-200 rounded-full mt-1 overflow-hidden">
                      <div 
                        className="h-full bg-blue-600 rounded-full"
                        style={{ width: `${(customer.totalSpent / (stats.customerMetrics[0]?.totalSpent || 1)) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Key Performance Indicators</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border border-slate-200 rounded-lg">
              <p className="text-sm text-slate-600 mb-1">Average Invoice Value</p>
              <p className="text-2xl font-bold text-slate-900">RM {stats.avgInvoiceValue.toFixed(2)}</p>
              <p className="text-xs text-slate-500 mt-1">Per transaction</p>
            </div>

            <div className="p-4 border border-slate-200 rounded-lg">
              <p className="text-sm text-slate-600 mb-1">Average Project Value</p>
              <p className="text-2xl font-bold text-slate-900">RM {stats.avgProjectValue.toFixed(2)}</p>
              <p className="text-xs text-slate-500 mt-1">Per project</p>
            </div>

            <div className="p-4 border border-slate-200 rounded-lg">
              <p className="text-sm text-slate-600 mb-1">Profit Margin</p>
              <p className={`text-2xl font-bold ${stats.profitMargin >= 20 ? 'text-green-600' : stats.profitMargin >= 10 ? 'text-yellow-600' : 'text-red-600'}`}>
                {stats.profitMargin.toFixed(1)}%
              </p>
              <p className="text-xs text-slate-500 mt-1">Overall margin</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
